import { useEffect, useState, useRef } from "react";
import {
    FaCalendarAlt,
    FaEnvelope,
    FaFacebookF,
    FaInstagram,
    FaMapMarkerAlt,
    FaMountain,
    FaPhone,
    FaUsers
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import API_BASE_URL from "../config";
import "./css/Booking.css";

function Booking() {
    const location = useLocation();
    const navigate = useNavigate();
    
    const data = location.state || {};
    const villa = data.villa || null;
    
    // Menangkap data room/kamar yang dipilih dari halaman detail
    const selectedRoom = data.room || data.selectedRoom || null; 
    
    // Menangkap string tanggal ISO (YYYY-MM-DD) yang dikirim dari halaman detail
    const passedCheckInISO = data.checkInISO || "";
    const passedCheckOutISO = data.checkOutISO || "";

    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [whatsapp, setWhatsapp] = useState("");
    const [loading, setLoading] = useState(false);

    // Membuat referensi (refs) untuk mengontrol input date secara langsung
    const checkInInputRef = useRef(null);
    const checkOutInputRef = useRef(null);

    // Otomatis set tanggal berformat ISO agar terbaca sempurna oleh input type="date"
    useEffect(() => {
        if (passedCheckInISO) {
            setCheckIn(passedCheckInISO);
        }
        if (passedCheckOutISO) {
            setCheckOut(passedCheckOutISO);
        }
    }, [passedCheckInISO, passedCheckOutISO]);

    const getNights = () => {
        if (!checkIn || !checkOut) return 0;
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    // Otomatis menggunakan harga kamar jika ada, jika tidak ada baru gunakan harga villa default
    const pricePerNight = Number(selectedRoom?.price_raw || villa?.price) || 0; 
    const nights = getNights();
    const totalPrice = nights * pricePerNight;
    
    // Otomatis menggunakan gambar kamar jika ada, jika tidak ada gunakan gambar villa default
    const villaImage = villa?.img || "https://via.placeholder.com/300x200?text=No+Image";

    // Fungsi untuk memicu penampil kalender bawaan saat ikon diklik
    const handleIconClick = (ref) => {
        if (ref.current && typeof ref.current.showPicker === "function") {
            ref.current.showPicker();
        } else if (ref.current) {
            ref.current.focus(); // Fallback untuk browser versi lama
        }
    };

    // 🔥 FUNGSI HANDLE PAYMENT (SUDAH DIPERBAIKI SANGAT AMAN)
    const handlePayment = async () => {
        if (!fullName || !email || !whatsapp || !checkIn || !checkOut) {
            alert("Harap lengkapi semua formulir!");
            return;
        }

        const finalVillaId = villa?.id || data?.id;
        if (!finalVillaId) {
            alert("ID Villa tidak ditemukan, silakan pilih villa kembali.");
            return;
        }

        setLoading(true);

        const bookingData = {
            villa_id: finalVillaId,
            villa_name: villa?.name || "Villa",
            room_name: selectedRoom?.bed_info || "", 
            check_in: checkIn,
            check_out: checkOut,
            guests: guests,
            full_name: fullName,
            email: email,
            whatsapp: whatsapp,
            total_price: totalPrice,
            payment_method: "Pending",
            payment_type: "Pending",
            status: "Pending",
            // 🌟 KUNCI PERBAIKAN: Menyisipkan link gambar ke dalam payload bookingData 🌟
            villa_img: villaImage 
        };

        try {
            const response = await fetch(`${API_BASE_URL}/api/bookings`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(bookingData)
            });

            const result = await response.json();

            if (response.ok) {
                // 🌟 KUNCI PERBAIKAN 2: Mengirim bookingData dan objek villa utuh ke halaman Payment 🌟
                navigate("/payment", { 
                    state: { 
                        bookingData: bookingData,
                        villa: villa 
                    } 
                });
            } else {
                alert("Gagal menyimpan: " + result.error);
            }
        } catch (error) {
            console.error("Error:", error);
            alert("Terjadi kesalahan koneksi ke server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="booking-page">
            {/* NAVBAR UTAMA */}
            <nav className="navbar">
                <div className="logo">
                    <div className="logo-icon"><FaMountain /></div>
                    <div className="logo-text">
                        <h3>Permata Wisata</h3>
                        <span>Jogja Tourism</span>
                    </div>
                </div>
                <div className="nav-menu">
                    <Link to="/">Home</Link>
                    <Link to="/villas">Villas</Link>
                    <a
                        href="https://wa.me/081216687367?text=Halo%20admin%20Permata%20Wisata,%20saya%20ingin%20bertanya%20tentang%20booking%20villa"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-btn"
                    >
                        Hubungi Kami
                    </a>
                </div>
            </nav>

            <div className="booking-header">
                <Link 
                    to="#" 
                    className="back-link" 
                    onClick={(e) => { 
                        e.preventDefault(); 
                        navigate(-1); 
                    }}
                >
                    &larr; Back to Villa Details
                </Link>
                <h1>Complete Your Booking</h1>
            </div>

            <div className="booking-container">
                <div className="booking-form">
                    <h3>Booking Details</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label onClick={() => handleIconClick(checkInInputRef)} style={{ cursor: "pointer" }}>
                                <FaCalendarAlt /> Check-in
                            </label>
                            <input 
                                type="date" 
                                ref={checkInInputRef}
                                value={checkIn} 
                                onChange={(e) => setCheckIn(e.target.value)} 
                            />
                        </div>
                        <div className="form-group">
                            <label onClick={() => handleIconClick(checkOutInputRef)} style={{ cursor: "pointer" }}>
                                <FaCalendarAlt /> Check-out
                            </label>
                            <input 
                                type="date" 
                                ref={checkOutInputRef}
                                value={checkOut} 
                                onChange={(e) => setCheckOut(e.target.value)} 
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label><FaUsers /> Guests</label>
                        <input
                            type="number"
                            min="1"
                            value={guests}
                            onChange={(e) => setGuests(e.target.value === "" ? "" : parseInt(e.target.value))}
                        />
                    </div>

                    <h3 className="contact-title">Contact Information</h3>
                    <div className="form-group">
                        <label>Full Name</label>
                        <input type="text" placeholder="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label><FaEnvelope /> Email</label>
                        <input type="email" placeholder="email@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label><FaPhone /> WhatsApp Number</label>
                        <input type="text" placeholder="+62..." value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} />
                    </div>

                    <button className="payment-btn" onClick={handlePayment} disabled={loading}>
                        {loading ? "Processing..." : "Continue to Payment"}
                    </button>
                </div>

                <div className="booking-summary">
                    <h3>Booking Summary</h3>
                    <div className="summary-card">
                        <img src={villaImage} alt={villa?.name} className="summary-img" />
                        <h4>{villa?.name || "Nama Villa Tidak Terbaca"}</h4>
                        <p className="location"><FaMapMarkerAlt /> {villa?.location || "Lokasi tidak tersedia"}</p>

                        <div className="summary-row">
                            <span>Harga / Malam</span>
                            <span>Rp {pricePerNight.toLocaleString('id-ID')}</span>
                        </div>
                        <div className="summary-row">
                            <span>Durasi</span>
                            <span>{nights} malam</span>
                        </div>
                        <hr />
                        <div className="summary-row total">
                            <span>Total</span>
                            <span className="price">Rp {totalPrice.toLocaleString('id-ID')}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* FOOTER */}
            <footer className="footer-container">
                <div className="footer-content">
                    <div className="footer-section footer-left">
                        <h2>Permata Wisata</h2>
                        <span>Jogja Tourism</span>
                        <p>Your trusted partner for villa and homestay bookings in Jogja.</p>
                    </div>
                    <div className="footer-section footer-center">
                        <h3>Contact Us</h3>
                        <p><FaEnvelope /> info@permatawisata.com</p>
                        <p><FaPhone /> +62 812-3456-7890</p>
                    </div>
                    <div className="footer-section footer-right">
                        <h3>Follow Us</h3>
                        <div className="social-icons">
                            <span className="social-icon"><FaInstagram /></span>
                            <span className="social-icon"><FaFacebookF /></span>
                        </div>
                    </div>
                </div>
                <hr className="footer-divider" />
                <div className="footer-bottom">
                    &copy; 2026 Permata Wisata. All rights reserved.
                </div>
            </footer>
        </div>
    );
}

export default Booking;